import { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { MonitorSmartphone, Plus, Trash2, RefreshCw, KeyRound, Lock, Download, History, Copy, ShieldCheck, AlertTriangle, ShieldAlert } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none w-full mt-1";
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CMD_STYLE = { pending: "bg-amber-50 text-amber-700 border-amber-200", sent: "bg-sky-50 text-sky-700 border-sky-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200", failed: "bg-red-50 text-red-700 border-red-200" };

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  }
};

export default function Mdm() {
  const [config, setConfig] = useState(null);
  const [devices, setDevices] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [editor, setEditor] = useState(null);
  const [history, setHistory] = useState(null);
  const [tempPw, setTempPw] = useState(null);

  const load = useCallback(() => {
    api.get("/mdm/config").then((r) => setConfig(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/mdm/devices").then((r) => setDevices(r.data)).catch(() => {});
    api.get("/mdm/compliance").then((r) => setCompliance(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addDevice = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mdm/devices", editor);
      toast.success("Device enrolled");
      setEditor(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const cmd = async (d, type) => {
    let app_name = "";
    if (type === "install_app" || type === "uninstall_app") {
      app_name = window.prompt(`App / software to ${type === "install_app" ? "install" : "uninstall"} on ${d.name}:`) || "";
      if (!app_name.trim()) return;
    }
    if (type === "wipe" && !window.confirm(`Wipe ${d.name}? This queues a full device wipe.`)) return;
    try {
      const { data } = await api.post(`/mdm/devices/${d.id}/commands`, { type, app_name });
      if (type === "reset_password") setTempPw({ device: d.name, pw: data.temp_password });
      else toast.success(`${type.replace(/_/g, " ")} queued for ${d.name} — runs at next check-in`);
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const simulate = async (d) => {
    try {
      const { data } = await api.post(`/mdm/devices/${d.id}/simulate-checkin`);
      toast.success(`${d.name} checked in — ${data.completed} command(s) executed`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openHistory = async (d) => {
    try {
      const { data } = await api.get(`/mdm/devices/${d.id}/commands`);
      setHistory({ device: d, cmds: data });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotate enrollment key? The old key stops working immediately.")) return;
    try {
      const { data } = await api.post("/mdm/config/rotate-key");
      setConfig((c) => ({ ...c, enroll_key: data.enroll_key }));
      toast.success("Enrollment key rotated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Unenroll and remove ${d.name}?`)) return;
    await api.delete(`/mdm/devices/${d.id}`).catch((e) => toast.error(fmtErr(e)));
    load();
  };

  return (
    <div data-testid="desk-mdm-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><MonitorSmartphone size={22} /> Device Management</h1>
        <button data-testid="mdm-add-device" onClick={() => setEditor({ name: "", os: "windows", os_version: "", device_user: "" })}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
          <Plus size={15} /> Enroll device
        </button>
      </div>
      {config && (
        <div className="bg-[#0B1F33] text-white rounded-2xl p-4 mb-5 text-xs flex flex-wrap items-center gap-2" data-testid="mdm-enroll-panel">
          <span className="font-semibold">Agent enrollment:</span>
          <code className="bg-white/10 rounded px-2 py-1">POST {API_BASE}/api/mdm/enroll</code>
          <span className="font-semibold ml-1">X-Enroll-Key:</span>
          <code className="bg-white/10 rounded px-2 py-1" data-testid="mdm-enroll-key">{config.enroll_key}</code>
          <button data-testid="mdm-copy-key" aria-label="Copy enrollment key"
            onClick={async () => { (await copyText(config.enroll_key)) ? toast.success("Key copied") : toast.error("Copy failed — select the key manually"); }}
            className="p-1.5 bg-white/10 hover:bg-white/20 rounded"><Copy size={12} /></button>
          <button data-testid="mdm-rotate-key" onClick={rotateKey} aria-label="Rotate enrollment key"
            className="p-1.5 bg-white/10 hover:bg-white/20 rounded" title="Rotate enrollment key"><RefreshCw size={12} /></button>
          <span className="text-white/60 w-full">Devices poll <code className="bg-white/10 rounded px-1">POST /api/mdm/checkin</code> (X-Device-Token) for queued commands and ack results. Use "Simulate check-in" to demo without a real agent.</span>
        </div>
      )}
      {compliance && compliance.summary.total > 0 && (
        <div className="mb-5" data-testid="mdm-compliance-panel">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {[["total", "Devices", MonitorSmartphone, "text-neutral-700 border-neutral-200 bg-white"],
              ["healthy", "Healthy", ShieldCheck, "text-emerald-700 border-emerald-200 bg-emerald-50"],
              ["warning", "Warning", AlertTriangle, "text-amber-700 border-amber-200 bg-amber-50"],
              ["critical", "Critical", ShieldAlert, "text-red-700 border-red-200 bg-red-50"]].map(([k, label, Icon, cls]) => (
              <div key={k} data-testid={`mdm-compliance-${k}`} className={`border rounded-2xl p-4 ${cls}`}>
                <div className="flex items-center gap-2 text-xs font-medium"><Icon size={14} /> {label}</div>
                <p className="text-2xl font-bold mt-1">{compliance.summary[k]}</p>
              </div>
            ))}
          </div>
          {compliance.devices.filter((d) => d.health !== "healthy").length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-2xl p-4" data-testid="mdm-flagged-list">
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Flagged devices</p>
              <ul className="space-y-2">
                {compliance.devices.filter((d) => d.health !== "healthy").map((d) => (
                  <li key={d.id} data-testid={`mdm-flagged-${d.id}`} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className={`text-xs font-semibold border rounded-full px-2 py-0.5 ${d.health === "critical" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{d.health}</span>
                    <span className="font-medium">{d.name}</span>
                    <span className="text-xs text-neutral-500">{d.issues.join(" · ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="space-y-3" data-testid="mdm-device-list">
        {devices.length === 0 && <p className="text-sm text-neutral-400" data-testid="mdm-empty">No devices enrolled yet.</p>}
        {devices.map((d) => (
          <div key={d.id} data-testid={`mdm-device-${d.id}`} className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="font-semibold text-sm">{d.name} <span className="text-xs text-neutral-400 font-normal">{d.os} {d.os_version}</span></p>
              <p className="text-xs text-neutral-400 mt-0.5">
                {d.device_user ? `User: ${d.device_user} · ` : ""}
                {d.last_checkin ? `Last check-in ${new Date(d.last_checkin).toLocaleString()}` : "Never checked in"}
                {d.pending_commands > 0 && <span className="ml-2 text-amber-700 font-semibold">{d.pending_commands} pending</span>}
              </p>
            </div>
            <button data-testid={`mdm-install-${d.id}`} onClick={() => cmd(d, "install_app")} title="Install app/software"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50"><Download size={12} /> Install app</button>
            <button data-testid={`mdm-resetpw-${d.id}`} onClick={() => cmd(d, "reset_password")} title="Reset device password"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50"><KeyRound size={12} /> Reset password</button>
            <button data-testid={`mdm-lock-${d.id}`} onClick={() => cmd(d, "lock")} title="Lock device"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-amber-300 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-50"><Lock size={12} /> Lock</button>
            <button data-testid={`mdm-wipe-${d.id}`} onClick={() => cmd(d, "wipe")} title="Wipe device"
              className="px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-xs font-medium hover:bg-red-50">Wipe</button>
            <button data-testid={`mdm-history-${d.id}`} onClick={() => openHistory(d)} title="Command history"
              className="p-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50"><History size={13} /></button>
            <button data-testid={`mdm-checkin-${d.id}`} onClick={() => simulate(d)} title="Simulate device check-in (demo)"
              className="p-1.5 border border-sky-300 text-sky-700 rounded-md hover:bg-sky-50"><RefreshCw size={13} /></button>
            <button data-testid={`mdm-remove-${d.id}`} onClick={() => remove(d)} title="Remove device"
              className="p-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      {editor && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="mdm-enroll-modal">
          <form onSubmit={addDevice} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-sm w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-4">Enroll device</h3>
            <label className="text-sm font-medium text-neutral-700">Device name</label>
            <input data-testid="mdm-name-input" required minLength={2} value={editor.name} onChange={(e) => setEditor((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <label className="text-xs text-neutral-500">OS
                <select data-testid="mdm-os-input" value={editor.os} onChange={(e) => setEditor((f) => ({ ...f, os: e.target.value }))} className={inputCls}>
                  {(config?.os_list || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select></label>
              <label className="text-xs text-neutral-500">OS version
                <input data-testid="mdm-osver-input" value={editor.os_version} onChange={(e) => setEditor((f) => ({ ...f, os_version: e.target.value }))} className={inputCls} /></label>
            </div>
            <label className="text-xs text-neutral-500 block mt-2">Device user
              <input data-testid="mdm-user-input" value={editor.device_user} onChange={(e) => setEditor((f) => ({ ...f, device_user: e.target.value }))} className={inputCls} /></label>
            <div className="flex gap-2 mt-5">
              <button data-testid="mdm-enroll-save" className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Enroll</button>
              <button type="button" onClick={() => setEditor(null)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {tempPw && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="mdm-temppw-modal">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-sm w-full p-7 text-center">
            <KeyRound size={28} className="mx-auto text-emerald-600" />
            <h3 className="font-semibold font-heading text-lg mt-3">Temporary password for {tempPw.device}</h3>
            <p className="text-2xl font-mono font-bold mt-3 bg-neutral-50 border border-neutral-200 rounded-lg py-2" data-testid="mdm-temp-password">{tempPw.pw}</p>
            <p className="text-xs text-neutral-400 mt-2">Shown once — share it securely with the user. Applied at next device check-in.</p>
            <button data-testid="mdm-temppw-close" onClick={async () => { const ok = await copyText(tempPw.pw); setTempPw(null); ok ? toast.success("Password copied") : toast.error("Copy failed — note the password before closing"); }}
              className="mt-4 px-6 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Copy & close</button>
          </div>
        </div>
      )}

      {history && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="mdm-history-modal" onClick={() => setHistory(null)}>
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-lg w-full p-7 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold font-heading text-lg mb-4">{history.device.name} — command history</h3>
            {history.cmds.length === 0 && <p className="text-sm text-neutral-400">No commands yet.</p>}
            <ul className="space-y-2">
              {history.cmds.map((c) => (
                <li key={c.id} data-testid={`mdm-cmd-${c.id}`} className="flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-md px-4 py-2.5 text-sm">
                  <span className="font-medium flex-1">{c.type.replace(/_/g, " ")}{c.payload?.app_name ? ` — ${c.payload.app_name}` : ""}</span>
                  <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${CMD_STYLE[c.status]}`}>{c.status}</span>
                  <span className="text-xs text-neutral-400">{new Date(c.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
